import React from 'react';
import { LogEntryWithType } from '@/types/TInbox';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent } from '@/store/account/selector';
import { renderPSTDate } from '@/utils/helper';

interface LogsListProps {
	logs: LogEntryWithType[];
	selectedLog: LogEntryWithType | null;
	onSelectLog: (log: LogEntryWithType) => void;
	onDeleteLog: (log: LogEntryWithType) => void;
}

const LogsList: React.FC<LogsListProps> = ({ logs, selectedLog, onSelectLog, onDeleteLog }) => {
	const selectedAgent = useAppSelector(getSelectedAgent);

	const getStatusBadge = (type: string) => {
		switch (type) {
			case 'call':
				return (
					<span className="bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
						CALL
					</span>
				);
			case 'voicemail':
				return (
					<span className="bg-blue-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
						VOICEMAIL
					</span>
				);
			case 'text':
				return (
					<span className="bg-purple-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
						TEXT
					</span>
				);
			default:
				return null;
		}
	};

	const getStatusIndicator = (type: string, status: string) => {
		if (type === 'call' && status === 'answered') {
			return 'bg-green-500';
		}
		if (type === 'voicemail' && status === 'unread') {
			return 'bg-blue-500';
		}
		if (type === 'text') {
			return 'bg-purple-500';
		}
		if (type === 'call' && status === 'missed') {
			return 'bg-green-500';
		}
		return 'bg-gray-400';
	};

	const handleDeleteClick = (e: React.MouseEvent, log: LogEntryWithType) => {
		e.stopPropagation();
		onDeleteLog(log);
	};

	const getDisplayName = (log: LogEntryWithType) => {
		if (log.type === 'text' || log.type === 'voicemail') {
			return selectedAgent?.name || 'My First Agent';
		}
		return log.contact.name || 'Unknown Caller';
	};

	return (
		<div className="divide-y divide-gray-100">
			{logs?.map((log) => {
				return (
					<div
						key={log.id}
						onClick={() => onSelectLog(log)}
						className={`px-3 sm:px-4 py-3 sm:py-4 cursor-pointer hover:bg-gray-50 transition-colors relative ${
							selectedLog?.id === log.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
						}`}
					>
						{selectedLog?.id === log.id && (
							<div className="absolute left-0 top-0 w-1 h-full bg-blue-500" />
						)}

						<div className="flex items-start justify-between mb-2">
							<div className="flex items-center gap-2 min-w-0 flex-1">
								<div
									className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusIndicator(log.type, log.status)}`}
								/>
								<span className="font-semibold text-gray-900 text-sm truncate">
									{getDisplayName(log)}
								</span>
							</div>
							<button
								onClick={(e) => handleDeleteClick(e, log)}
								className="ml-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
								title="Delete log"
							>
								<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
									/>
								</svg>
							</button>
						</div>

						<div className="flex items-center justify-between mb-2">
							{getStatusBadge(log.type)}
							{log.type === 'call' && (
								<span className="text-xs text-gray-600 font-mono">{log.contact.phone}</span>
							)}
						</div>

						<p className="text-sm text-gray-700 leading-relaxed line-clamp-2 pr-2 mb-2">
							{log.message || 'No message content'}
						</p>

						<div className="flex justify-end">
							<span className="text-xs text-gray-500">
								{renderPSTDate(log.date, selectedAgent?.time_zone)}
							</span>
						</div>
					</div>
				);
			})}

			{logs.length === 0 && (
				<div className="p-8 text-center text-gray-500">
					<div className="mb-2">
						<svg
							className="mx-auto h-12 w-12 text-gray-300"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1}
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							/>
						</svg>
					</div>
					<p className="text-base font-medium text-gray-600">No logs found</p>
					<p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
				</div>
			)}
		</div>
	);
};

export default LogsList;
